import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { SISTEMAS_EM_ORDEM, nomeDoSistema } from "../sistemas";
import {
  IconPlus,
  IconSearch,
  IconTrash,
  IconClose,
  IconCheck,
  IconDownload,
  IconChevronRight,
  IconMessage,
  IconList,
} from "../icons";
import ErrorState from "./ErrorState";
import { dataBr } from "./PainelView";

/**
 * SOLICITAÇÕES — a fila de pedidos.
 *
 * Entram por dois caminhos: a aba "Solicitar Atualização" dentro de cada
 * sistema (origem "Sistema") e o registro à mão aqui (origem "Interna", para o
 * que chegou por WhatsApp ou no corredor). A tela é a mesma para os dois — o
 * que muda é o selo de origem, que diz se alguém está esperando resposta pelo
 * sistema.
 */

const TIPOS = ["Adaptação", "Nova função", "Correção", "Configuração", "Dúvida"];
const PRIORIDADES = ["Baixa", "Normal", "Alta", "Urgente"];
const STATUS = [
  "Recebida",
  "Em análise",
  "Em desenvolvimento",
  "Aguardando retorno",
  "Concluída",
  "Recusada",
];

/** Cor do selo conforme o andamento — verde só quando terminou de verdade. */
function pillStatus(status: string): string {
  if (status === "Concluída") return "pill pill-ok";
  if (status === "Recusada") return "pill pill-neutral";
  if (status === "Em desenvolvimento") return "pill pill-warn";
  return "pill pill-neutral";
}

export default function SolicitacoesView({
  onMudou,
  somenteLeitura = false,
}: {
  onMudou: () => void;
  /** Somente visualização (Portal): o status da lista grava ao trocar — fica travado. */
  somenteLeitura?: boolean;
}) {
  const [linhas, setLinhas] = useState<any[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState("abertas");
  const [filtroSistema, setFiltroSistema] = useState("todos");
  const [busca, setBusca] = useState("");
  const [abrindo, setAbrindo] = useState<number | null>(null);
  const [nova, setNova] = useState(false);
  /* localStorage pode LANÇAR (aba anônima, navegador com dados bloqueados) —
     por isso a leitura vem embrulhada e o quadro é o padrão. */
  const [visao, setVisao] = useState<"quadro" | "lista">(() => {
    try {
      return localStorage.getItem("dev-solic-visao") === "lista" ? "lista" : "quadro";
    } catch {
      return "quadro";
    }
  });

  function trocarVisao(v: "quadro" | "lista") {
    setVisao(v);
    try {
      localStorage.setItem("dev-solic-visao", v);
    } catch {
      /* sem storage: a escolha vale só nesta visita */
    }
  }

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const filtros: Record<string, string> = { sistema: filtroSistema, q: busca };
      // "abertas" não é um status do banco: é o recorte do dia a dia (tudo o que
      // ainda não terminou). Por isso ele é filtrado aqui, e não na consulta.
      if (filtroStatus !== "abertas" && filtroStatus !== "todos") filtros.status = filtroStatus;
      const todas = (await api.solicitacoes.listar(filtros)) as any[];
      /* No QUADRO, "em aberto" guarda também as concluídas RECENTES: a última
         coluna existe para mostrar o que acabou de sair, e ela ficaria sempre
         vazia se toda conclusão sumisse na hora. Uma semana é o bastante para
         a pessoa ver o que entregou — depois disso o pedido vive na Lista. */
      const recente = (s: any) =>
        s.status === "Concluída" && Number(s.dias_parada ?? 99) <= 7 && visao === "quadro";
      setLinhas(
        filtroStatus === "abertas"
          ? todas.filter(
              (s) => (s.status !== "Concluída" && s.status !== "Recusada") || recente(s),
            )
          : todas,
      );
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível carregar as solicitações.");
    } finally {
      setCarregando(false);
    }
  }, [filtroStatus, filtroSistema, busca, visao]);

  useEffect(() => {
    const t = setTimeout(carregar, busca ? 350 : 0); // digitação não dispara a cada tecla
    return () => clearTimeout(t);
  }, [carregar, busca]);

  async function mudarStatus(id: number, status: string) {
    try {
      await api.solicitacoes.atualizar(id, { status });
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao mudar o status.");
    }
  }

  async function excluir(s: any) {
    if (!confirm("Apagar a solicitação \"" + s.titulo + "\"?")) return;
    try {
      await api.solicitacoes.remover(s.id);
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao excluir.");
    }
  }

  return (
    <>
      <div className="crud-toolbar">
        <div className="search-wrap">
          <span className="search-icone">
            <IconSearch size={16} />
          </span>
          <input
            className="search"
            placeholder="Buscar por título, texto ou quem pediu…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
            <option value="abertas">Em aberto</option>
            <option value="todos">Todas</option>
            {STATUS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select value={filtroSistema} onChange={(e) => setFiltroSistema(e.target.value)}>
            <option value="todos">Todos os sistemas</option>
            {SISTEMAS_EM_ORDEM.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
              </option>
            ))}
          </select>
          <button
            className="btn-secondary btn-sm"
            title="Baixar em planilha o que está filtrado"
            onClick={() =>
              window.open(
                api.solicitacoes.urlExportar({
                  sistema: filtroSistema,
                  status: filtroStatus === "abertas" || filtroStatus === "todos" ? "" : filtroStatus,
                }),
                "_blank",
                "noopener",
              )
            }
          >
            <IconDownload size={15} /> Exportar
          </button>
          <button className="btn-primary" onClick={() => setNova(true)}>
            <IconPlus size={16} /> Nova solicitação
          </button>
        </div>
      </div>

      {/* QUADRO × LISTA — o quadro é a visão do dia (o que está em cada etapa);
          a lista continua para quem quer ordenar, procurar e conferir campo a
          campo. A escolha fica guardada no navegador porque cada pessoa usa de
          um jeito e trocar de visão a cada abertura cansa. */}
      <div className="triagem-abas">
        <button className={visao === "quadro" ? "ativa" : ""} onClick={() => trocarVisao("quadro")}>
          Quadro
        </button>
        <button className={visao === "lista" ? "ativa" : ""} onClick={() => trocarVisao("lista")}>
          Lista
        </button>
      </div>

      {erro ? (
        <ErrorState message={erro} onRetry={carregar} />
      ) : carregando ? (
        <div className="carregando">Carregando…</div>
      ) : linhas.length === 0 ? (
        <div className="empty">
          <IconList size={30} />
          Nenhuma solicitação por aqui. As que chegarem pela aba{" "}
          <strong>Solicitar Atualização</strong> dos sistemas caem direto nesta lista.
        </div>
      ) : visao === "quadro" ? (
        <Quadro
          linhas={linhas}
          onAbrir={(id) => setAbrindo(id)}
          onMover={(id, status) => mudarStatus(id, status)}
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>O que foi pedido</th>
                <th>Sistema</th>
                <th>Tipo</th>
                <th>Prioridade</th>
                <th>Status</th>
                <th>Quem pediu</th>
                <th>Prazo</th>
                <th className="col-actions" />
              </tr>
            </thead>
            <tbody>
              {linhas.map((s) => (
                <tr key={s.id}>
                  <td>{s.id}</td>
                  <td>
                    <button className="btn-link" onClick={() => setAbrindo(s.id)}>
                      {s.titulo}
                    </button>
                    {s.comentarios > 0 && (
                      <small className="muted"> · {s.comentarios} comentário(s)</small>
                    )}
                  </td>
                  <td>{nomeDoSistema(s.sistema)}</td>
                  <td>{s.tipo}</td>
                  <td>
                    <span className={"prio prio-" + String(s.prioridade).toLowerCase()}>
                      {s.prioridade}
                    </span>
                  </td>
                  <td>
                    <select
                      className="dev-status-select"
                      value={s.status}
                      disabled={somenteLeitura}
                      onChange={(e) => mudarStatus(s.id, e.target.value)}
                    >
                      {STATUS.map((x) => (
                        <option key={x} value={x}>
                          {x}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {s.solicitante || "—"}
                    <br />
                    <small className="muted">
                      {s.origem === "Sistema" ? "pelo sistema" : "registrada aqui"}
                    </small>
                  </td>
                  <td>{s.prazo ? dataBr(s.prazo) : "—"}</td>
                  <td className="col-actions">
                    <button
                      className="icon-btn icon-btn-danger"
                      title="Excluir"
                      onClick={() => excluir(s)}
                    >
                      <IconTrash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(nova || abrindo !== null) && (
        <SolicitacaoModal
          id={abrindo ?? undefined}
          onClose={() => {
            setNova(false);
            setAbrindo(null);
          }}
          onSaved={async () => {
            await carregar();
            onMudou();
          }}
        />
      )}
    </>
  );
}

/* ==================== O cartão da solicitação ==================== */

function SolicitacaoModal({
  id,
  onClose,
  onSaved,
}: {
  id?: number;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [dados, setDados] = useState<any>({
    titulo: "",
    descricao: "",
    sistema: "",
    tipo: TIPOS[0],
    prioridade: "Normal",
    status: "Recebida",
    solicitante: "",
    email: "",
    telefone: "",
    prazo: "",
    resposta: "",
  });
  const [comentarios, setComentarios] = useState<any[]>([]);
  const [novoComentario, setNovoComentario] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async (solicitacaoId: number) => {
    try {
      const s = await api.solicitacoes.obter(solicitacaoId);
      setDados({
        ...s,
        descricao: s.descricao ?? "",
        sistema: s.sistema ?? "",
        solicitante: s.solicitante ?? "",
        email: s.email ?? "",
        telefone: s.telefone ?? "",
        prazo: s.prazo ? String(s.prazo).slice(0, 10) : "",
        resposta: s.resposta ?? "",
      });
      setComentarios(s.comentarios ?? []);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Erro ao carregar.");
    }
  }, []);

  useEffect(() => {
    if (id) carregar(id);
  }, [id, carregar]);

  async function salvar() {
    setErro(null);
    if (!String(dados.titulo).trim()) {
      setErro("Escreva o que precisa ser feito.");
      return;
    }
    setSalvando(true);
    try {
      const corpo = {
        titulo: dados.titulo,
        descricao: dados.descricao,
        sistema: dados.sistema,
        tipo: dados.tipo,
        prioridade: dados.prioridade,
        status: dados.status,
        solicitante: dados.solicitante,
        email: dados.email,
        telefone: dados.telefone,
        prazo: dados.prazo,
        resposta: dados.resposta,
      };
      if (id) await api.solicitacoes.atualizar(id, corpo);
      else await api.solicitacoes.criar(corpo);
      await onSaved();
      onClose();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível salvar.");
      setSalvando(false);
    }
  }

  async function comentar() {
    if (!id || !novoComentario.trim()) return;
    try {
      await api.solicitacoes.comentar(id, novoComentario.trim());
      setNovoComentario("");
      await carregar(id);
      await onSaved();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao comentar.");
    }
  }

  const set = (campo: string) => (e: any) => setDados({ ...dados, [campo]: e.target.value });

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-lg" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>
              {id ? "Solicitação " + (dados.protocolo ?? "#" + id) : "Nova solicitação"}
            </h3>
            <p className="modal-sub">
              {id && dados.origem === "Sistema"
                ? "Chegou pela aba Solicitar Atualização — alguém está esperando retorno."
                : "Registre aqui o pedido que chegou por fora do sistema."}
            </p>
            {/* O LINK DE ACOMPANHAMENTO é a resposta pronta para "e aquele meu
                pedido?": em vez de explicar por WhatsApp, manda-se o endereço,
                que mostra a etapa atual e a resposta quando ela existir. */}
            {id && dados.protocolo && (
              <button
                type="button"
                className="btn-link"
                title="Copiar o link que a pessoa usa para acompanhar"
                onClick={async () => {
                  const url = window.location.origin + "/pedido/" + dados.protocolo;
                  try {
                    await navigator.clipboard.writeText(url);
                    alert("Link copiado:\n" + url);
                  } catch {
                    prompt("Copie o link de acompanhamento:", url);
                  }
                }}
              >
                copiar link de acompanhamento
              </button>
            )}
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="form-grid">
            <label className="field field-wide">
              <span>
                O que precisa ser feito <span className="req">*</span>
              </span>
              <input value={dados.titulo} onChange={set("titulo")} autoFocus />
            </label>
            <label className="field field-wide">
              <span>Detalhes</span>
              <textarea rows={4} value={dados.descricao} onChange={set("descricao")} />
            </label>
            <label className="field">
              <span>Sistema</span>
              <select value={dados.sistema} onChange={set("sistema")}>
                <option value="">Geral / vários</option>
                {SISTEMAS_EM_ORDEM.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Tipo</span>
              <select value={dados.tipo} onChange={set("tipo")}>
                {TIPOS.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Prioridade</span>
              <select value={dados.prioridade} onChange={set("prioridade")}>
                {PRIORIDADES.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Status</span>
              <select value={dados.status} onChange={set("status")}>
                {STATUS.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Quem pediu</span>
              <input value={dados.solicitante} onChange={set("solicitante")} />
            </label>
            <label className="field">
              <span>E-mail</span>
              <input value={dados.email} onChange={set("email")} inputMode="email" />
            </label>
            <label className="field">
              <span>WhatsApp</span>
              <input value={dados.telefone} onChange={set("telefone")} inputMode="tel" />
            </label>
            <label className="field">
              <span>Prazo combinado</span>
              <input type="date" value={dados.prazo} onChange={set("prazo")} />
            </label>
            <label className="field field-wide">
              <span>Resposta ao solicitante (o que foi feito)</span>
              <textarea rows={3} value={dados.resposta} onChange={set("resposta")} />
              {dados.respondido_por && (
                <small className="muted">
                  Respondido por {dados.respondido_por} em {dataBr(dados.respondido_em)}
                </small>
              )}
            </label>
          </div>

          {id && (
            <div className="dev-comentarios">
              <h4 className="section-title">Andamento</h4>
              {comentarios.length === 0 ? (
                <p className="muted">Sem comentários ainda.</p>
              ) : (
                <ul className="dev-lista">
                  {comentarios.map((c) => (
                    <li key={c.id}>
                      <span className="dev-lista-main">
                        <strong>{c.texto}</strong>
                        <small className="muted">
                          {c.autor || "sistema"} · {dataBr(c.criado_em)}
                        </small>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="dev-comentar">
                <input
                  placeholder="Escreva o andamento…"
                  value={novoComentario}
                  onChange={(e) => setNovoComentario(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") comentar();
                  }}
                />
                <button className="btn-secondary btn-sm" onClick={comentar}>
                  <IconCheck size={15} /> Anotar
                </button>
              </div>
            </div>
          )}

          {erro && <div className="form-error">{erro}</div>}
        </div>

        <div className="modal-foot">
          <button className="btn-secondary" onClick={onClose} disabled={salvando}>
            Fechar
          </button>
          <button className="btn-primary" onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando…" : id ? "Salvar alterações" : "Registrar solicitação"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==================== O QUADRO ==================== */

/** As colunas do quadro, na ordem em que um pedido caminha. */
const COLUNAS = ["Recebida", "Em análise", "Em desenvolvimento", "Aguardando retorno", "Concluída"];

/** O passo seguinte de cada etapa — é o que o botão "→" do cartão faz. */
const PROXIMO: Record<string, string> = {
  Recebida: "Em análise",
  "Em análise": "Em desenvolvimento",
  "Em desenvolvimento": "Aguardando retorno",
  "Aguardando retorno": "Concluída",
};

/**
 * QUADRO — a fila em etapas, como se vê num quadro de post-its.
 *
 * Por que clique e não arrastar: o quadro é usado no celular tanto quanto no
 * computador (o desenvolvedor olha a fila fora da mesa), e arrastar cartão em
 * tela pequena erra mais do que acerta. O botão "→" leva para a etapa seguinte,
 * e o cartão inteiro abre o pedido para qualquer outra mudança.
 *
 * "Recusada" não vira coluna de propósito: é um fim de linha raro e ocuparia um
 * quinto da largura para mostrar quase nada. Esses pedidos aparecem na Lista.
 */
function Quadro({
  linhas,
  onAbrir,
  onMover,
}: {
  linhas: any[];
  onAbrir: (id: number) => void;
  onMover: (id: number, status: string) => void;
}) {
  return (
    <div className="dev-quadro">
      {COLUNAS.map((coluna) => {
        const doStatus = linhas.filter((s) => s.status === coluna);
        return (
          <div className="dev-quadro-col" key={coluna}>
            <div className="dev-quadro-cab">
              <span>{coluna}</span>
              <small>{doStatus.length}</small>
            </div>

            {doStatus.length === 0 ? (
              <p className="dev-quadro-vazio">—</p>
            ) : (
              doStatus.map((s) => (
                <article
                  key={s.id}
                  className={
                    "dev-card-solic" +
                    (s.prioridade === "Urgente" ? " urgente" : "") +
                    (s.prioridade === "Alta" ? " alta" : "") +
                    (s.atrasada ? " atrasada" : "")
                  }
                  onClick={() => onAbrir(s.id)}
                >
                  <header>
                    <span className="dev-card-prot">{s.protocolo ?? "#" + s.id}</span>
                    {/* O selo acompanha a prioridade (a mesma escala do resto
                        do sistema): antes toda prioridade diferente de Normal
                        saía com o MESMO vermelho, e "Baixa" gritava igual a
                        "Urgente". */}
                    {s.prioridade !== "Normal" && (
                      <span className={"dev-card-prio prio-" + String(s.prioridade).toLowerCase()}>
                        {s.prioridade}
                      </span>
                    )}
                  </header>
                  <strong>{s.titulo}</strong>
                  <small className="muted">
                    {nomeDoSistema(s.sistema)} · {s.tipo}
                    {s.solicitante ? " · " + s.solicitante : ""}
                  </small>
                  <footer>
                    {/* O tempo parado é o que denuncia o pedido esquecido —
                        por isso ele fica no cartão, e não só no relatório. */}
                    {/* `?? 0`: sem isso, um registro que chegue sem a contagem
                        (uma resposta antiga em cache, por exemplo) escreve
                        "há undefined dias" na cara de quem está usando. */}
                    {(() => {
                      const parada = Number(s.dias_parada ?? 0);
                      return (
                        <span className={parada >= 7 ? "dev-card-velho" : "muted"}>
                          {parada === 0 ? "hoje" : parada === 1 ? "há 1 dia" : "há " + parada + " dias"}
                        </span>
                      );
                    })()}
                    {s.atrasada && <span className="dev-card-atraso">prazo {dataBr(s.prazo)}</span>}
                    {s.comentarios > 0 && (
                      <span className="muted dev-card-coment">
                        <IconMessage size={13} /> {s.comentarios}
                      </span>
                    )}
                    {PROXIMO[coluna] && (
                      <button
                        className="dev-card-mover"
                        title={"Mover para " + PROXIMO[coluna]}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMover(s.id, PROXIMO[coluna]);
                        }}
                      >
                        <IconChevronRight size={15} />
                      </button>
                    )}
                  </footer>
                </article>
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}
